import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { magnitude, stamp, USD } from '../lib/format';
import { C, ink } from '../lib/theme';
import type { LostEvent } from '../lib/types';
import type { EventCorrectionBody } from './EventCard';
import { IconArrowRight, IconCheck } from './icons';
import { ScaleModal, type Origin } from './ScaleModal';
import { ErrorBanner, PrimaryButton, Skeleton } from './primitives';
import { useToast } from './Toast';

/**
 * Modal de Evidência (PRD §7.2): abas Link/Imagem, nota opcional, lista das
 * evidências já anexadas acima do formulário, e fluxo "adicionar e continuar"
 * (o modal NÃO fecha ao gravar). Erros aparecem inline sem fechar o modal e sem
 * perder o que já foi preenchido — exigência do §9-B.
 *
 * `onSetCorrection`/`onClearCorrection`: toggle "Corrigir" (pedido explícito
 * de morar dentro do modal de evidência, não no auditar) — liga duas zonas
 * minimalistas (valor do sistema, somente leitura · valor real, editável) e
 * grava em `revops_event_correction` via o mesmo mecanismo que o
 * `EventAuditModal` já usa. Os handlers vêm de quem abriu o modal
 * (`EventCard`, que já tem os da lista que o renderiza) — este componente é
 * montado uma vez só em `App.tsx`, sem lista própria pra atualizar sozinho.
 */
export function EvidenceModal({ event, origin, onClose, onCountChange, onSetCorrection, onClearCorrection }: {
  event: LostEvent | null;
  origin: Origin | null;
  onClose: () => void;
  onCountChange?: (eventId: number, delta: number) => void;
  onSetCorrection?: (ev: LostEvent, body: EventCorrectionBody) => void;
  onClearCorrection?: (ev: LostEvent) => void;
}) {
  const toast = useToast();
  const [tab, setTab] = useState<'link' | 'image'>('link');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const hasCorrection = event?.corrected_event_type != null;
  const [correcting, setCorrecting] = useState(false);
  const [realValue, setRealValue] = useState('');

  // Reinicia o estado da correção sempre que o evento muda — sem isso, abrir
  // "Evidência" de um card depois de outro herdaria o valor real digitado
  // pro evento anterior.
  useEffect(() => {
    setCorrecting(hasCorrection);
    setRealValue(hasCorrection ? String(magnitude(event!.corrected_mrr_lost)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const { data, loading, error, reload } = useAsync(
    () => (event ? api.listEvidence(event.id) : Promise.resolve([])),
    [event?.id],
  );

  // Defesa em profundidade: registros já gravados antes do validador do backend
  // continuam no banco, e React não sanitiza href — renderiza javascript:/data:
  // literalmente. Confere o esquema aqui também, não só confia no que o servidor aceitou.
  function linkSeguro(u: string | null): string | null {
    if (!u) return null;
    try {
      return ['http:', 'https:'].includes(new URL(u).protocol) ? u : null;
    } catch {
      return null;
    }
  }

  function resetForm() {
    setUrl(''); setNote(''); setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  function pickFile(f: File | null) {
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
    setInlineError(null);
  }

  async function submit() {
    if (!event) return;
    setInlineError(null);

    if (tab === 'link') {
      const trimmed = url.trim();
      // Validação de formato antes de gastar uma request (PRD §7.2) — mesmo
      // critério do backend (httpUrl): só http/https, não só "é uma URL válida".
      if (!linkSeguro(trimmed)) {
        setInlineError('URL inválida — precisa começar com http:// ou https://.');
        return;
      }
      setSaving(true);
      try {
        await api.addLinkEvidence(event.id, trimmed, note.trim() || undefined);
        onCountChange?.(event.id, 1);
        toast('Link anexado como evidência.', 'ok');
        resetForm(); reload();
      } catch (err) {
        setInlineError(err instanceof ApiError ? err.message : 'Falha ao anexar o link.');
      } finally { setSaving(false); }
      return;
    }

    if (!file) { setInlineError('Escolha uma imagem PNG, JPEG ou WEBP.'); return; }
    setSaving(true);
    try {
      await api.addImageEvidence(event.id, file, note.trim() || undefined);
      onCountChange?.(event.id, 1);
      toast('Imagem anexada como evidência.', 'ok');
      resetForm(); reload();
    } catch (err) {
      setInlineError(err instanceof ApiError ? err.message : 'Falha no upload.');
    } finally { setSaving(false); }
  }

  async function remove(id: number) {
    if (!event) return;
    try {
      await api.deleteEvidence(id);
      onCountChange?.(event.id, -1);
      toast('Evidência removida.', 'info');
      reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao remover.', 'error');
    }
  }

  // Sem toast de sucesso aqui de propósito — `onSetCorrection`/`onClearCorrection`
  // (vindos de `useEventList`) já fazem update otimista da lista e disparam o
  // toast de ERRO sozinhos se o request falhar (mesmo padrão que o
  // `EventAuditModal` já usa pra correção). Um toast de sucesso fixo aqui
  // mentiria quando o request por trás falhasse — foi assim que o bug real de
  // GRANT (`DELETE` faltando pra `revops_app` em `revops_event_correction`,
  // corrigido na migration 010) passou despercebido até eu testar de ponta a
  // ponta: a UI dizia "removido" mesmo com o backend rejeitando com 42501.
  function saveCorrection() {
    if (!event || !onSetCorrection) return;
    const n = Number(realValue.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) {
      toast('Digite um valor real válido.', 'error');
      return;
    }
    onSetCorrection(event, { corrected_event_type: event.event_type, corrected_mrr_lost: n });
  }

  function removeCorrection() {
    if (!event || !onClearCorrection) return;
    onClearCorrection(event);
    setRealValue('');
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 12.5, padding: '9px 12px',
    borderRadius: 10, border: `1px solid ${ink(0.14)}`, background: 'var(--input-bg)',
    color: C.fg, outline: 'none',
  };

  const count = data?.length ?? 0;

  // Toggle "Corrigir" mora no HEADER do modal agora (pedido explícito) — fica
  // visível mesmo com o corpo rolado, e não some espaço no corpo pra abrigar
  // seu próprio label+switch. Compacto de propósito: é chrome do modal, não
  // conteúdo.
  const headerToggle = event && onSetCorrection && (
    <button
      type="button" onClick={() => setCorrecting((v) => !v)}
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', padding: 0,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: correcting ? C.accent : ink(0.5) }}>Corrigir</span>
      <span style={{
        width: 28, height: 15, borderRadius: 99, flex: 'none', position: 'relative',
        background: correcting ? C.accent : ink(0.16), transition: 'background 0.18s ease',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: correcting ? 15 : 2, width: 11, height: 11, borderRadius: '50%',
          background: '#fff', transition: 'left 0.18s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }} />
      </span>
    </button>
  );

  return (
    <ScaleModal
      open={event !== null} origin={origin} onClose={() => { resetForm(); onClose(); }}
      title="Evidência" subtitle={event ? `${event.master_id} · evento #${event.id}` : ''} width={400}
      headerExtra={headerToggle}
    >
      {/* Zona "Corrigir valor" — agora a zona de mais destaque do corpo (o
          toggle que a liga saiu daqui, foi pro header), então os números
          ganharam tamanho/contraste de verdade em vez de recuar: não é mais
          uma feature secundária escondida, é o que aparece primeiro quando
          ligado. Ainda sem card/caixa — "mais destaque" veio de tipografia
          (tamanho, peso, contraste), não de moldura. */}
      {event && onSetCorrection && correcting && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: ink(0.58), fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
              {USD(magnitude(event.mrr_lost))}
            </span>
            <span style={{ color: ink(0.38), flex: 'none', display: 'flex', alignSelf: 'center' }}><IconArrowRight size={14} /></span>
            <input
              type="text" inputMode="decimal" value={realValue}
              onChange={(e) => setRealValue(e.target.value)}
              placeholder="valor real"
              style={{
                flex: 1, minWidth: 0, font: 'inherit', fontSize: 20, fontWeight: 800,
                color: C.green, border: 'none', borderBottom: `2px solid rgba(46,204,113,0.5)`,
                background: 'transparent', outline: 'none', padding: '2px 0',
                fontVariantNumeric: 'tabular-nums',
              }}
            />
            <button
              type="button" onClick={saveCorrection} title="Salvar valor real"
              style={{
                width: 28, height: 28, borderRadius: 8, flex: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: realValue ? 'rgba(46,204,113,0.16)' : ink(0.06),
                color: realValue ? C.green : ink(0.35),
              }}
            ><IconCheck size={13} /></button>
          </div>
          {hasCorrection && onClearCorrection && (
            <button
              type="button" onClick={removeCorrection}
              style={{
                border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', fontSize: 11,
                color: ink(0.38), padding: 0, marginTop: 7,
              }}
            >remover correção</button>
          )}
        </div>
      )}

      {loading && <Skeleton height={32} radius={10} />}
      {error && <ErrorBanner message={error} onRetry={reload} />}
      {/* Lista sem card por item — um item de lista não precisa de borda e
          fundo próprios pra se distinguir do vizinho, um traço fino já basta
          (nem tudo precisa de um card). E sem cabeçalho "Anexos" próprio —
          a lista fala por si (pedido explícito de remover essa zona/nome). */}
      {count > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(data ?? []).map((ev, i) => (
            <div key={ev.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
              borderTop: i > 0 ? `1px solid ${ink(0.06)}` : 'none',
            }}>
              <span style={{ fontSize: 12, flex: 'none' }}>{ev.type === 'image' ? '🖼' : '🔗'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {(() => {
                  const href = linkSeguro(ev.url);
                  const label = ev.type === 'image' ? (ev.storage_path ?? 'imagem') : ev.url;
                  if (!ev.url) return <span style={{ fontSize: 11.5, color: ink(0.4) }}>arquivo indisponível</span>;
                  if (!href) {
                    return (
                      <span style={{ fontSize: 11.5, color: ink(0.4) }} title="Link bloqueado por esquema não permitido">
                        {label}
                      </span>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" style={{
                      fontSize: 11.5, color: C.accent, textDecoration: 'none',
                      display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{label}</a>
                  );
                })()}
                <div style={{ fontSize: 10.5, color: ink(0.36), marginTop: 1 }}>
                  {stamp(ev.created_at)}{ev.note ? ` · ${ev.note}` : ''}
                </div>
              </div>
              <button type="button" onClick={() => remove(ev.id)} style={{
                border: 'none', background: 'none', cursor: 'pointer', font: 'inherit', fontSize: 10.5,
                color: ink(0.35), flex: 'none',
              }}>remover</button>
            </div>
          ))}
        </div>
      )}

      {/* Divisor só existe se tiver algo acima pra separar do formulário —
          sem lista e sem correção ativa, ele flutuaria sozinho com um vão
          morto em cima (espaço sem função, o oposto do que foi pedido). */}
      {(count > 0 || correcting) && <div style={{ height: 1, background: ink(0.07), margin: '14px 0' }} />}

      {/* Formulário de adicionar — a ação dominante da tela. Label + input
          ficam colados (sem tab acima "boiando" longe do campo que ela
          afeta), e "Adicionar" é o único botão cheio/gradiente do modal:
          tudo mais compete por MENOS peso visual, não por cor diferente. */}
      <div style={{ display: 'flex', gap: 4, background: ink(0.06), borderRadius: 10, padding: 3, marginBottom: 8 }}>
        {(['link', 'image'] as const).map((t) => (
          <button key={t} type="button" onClick={() => { setTab(t); setInlineError(null); }} style={{
            flex: 1, border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12, fontWeight: 700,
            padding: '6px 0', borderRadius: 8,
            background: tab === t ? ink(0.13) : 'transparent',
            color: tab === t ? C.fg : ink(0.5),
          }}>{t === 'link' ? 'Link' : 'Imagem'}</button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {tab === 'link' ? (
          <input
            type="url" value={url} onChange={(e) => { setUrl(e.target.value); setInlineError(null); }}
            placeholder="https://dashboard.stripe.com/subscriptions/sub_…" style={inputStyle}
          />
        ) : (
          <div>
            <input
              ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp"
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              style={{ ...inputStyle, padding: '9px 12px', cursor: 'pointer' }}
            />
            {preview && (
              <img src={preview} alt="Prévia" style={{
                marginTop: 8, maxWidth: '100%', maxHeight: 160, borderRadius: 10,
                border: `1px solid ${ink(0.12)}`, display: 'block',
              }} />
            )}
            <div style={{ fontSize: 11, color: ink(0.4), marginTop: 5 }}>PNG, JPEG ou WEBP · até 8MB</div>
          </div>
        )}

        <input
          type="text" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Nota (opcional)" style={inputStyle}
        />

        {inlineError && (
          <div style={{
            fontSize: 12, color: C.churn, background: 'rgba(242,85,90,0.1)',
            border: '1px solid rgba(242,85,90,0.28)', borderRadius: 10, padding: '8px 11px',
          }}>{inlineError}</div>
        )}

        <PrimaryButton onClick={submit} disabled={saving} style={{ width: '100%', fontSize: 13.5, padding: '11px 16px', marginTop: 2 }}>
          {saving ? 'gravando…' : 'Adicionar'}
        </PrimaryButton>
      </div>
    </ScaleModal>
  );
}
